import { useEffect, useRef, useCallback } from 'react'

export interface UseScrollIntoViewOnFocusFlowArgs {
  scrollPadding?: number // Extra padding at bottom
  offsetTop?: number // Fixed header height
  enableAnimation?: boolean // Smooth scrolling
  iosExtraDelay?: number // Additional delay for iOS keyboard
  animationDuration?: number // Max duration for smooth scrolling
  adjustForTextarea?: boolean // Auto-adjust for textarea growth
  scrollContainer?: Window | HTMLElement // Custom scroll container
}

export const useScrollIntoViewOnFocus = (options: UseScrollIntoViewOnFocusFlowArgs = {}) => {
  const {
    scrollPadding = 20, // Extra padding at bottom
    offsetTop = 60, // Fixed header height
    enableAnimation = true, // Smooth scrolling
    iosExtraDelay = 300, // Additional delay for iOS keyboard
    animationDuration = 300, // Max duration for smooth scrolling
    adjustForTextarea = true, // Auto-adjust for textarea growth
    scrollContainer = window, // Custom scroll container
  } = options

  const focusedInput = useRef<HTMLElement | null>(null)
  const resizeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isIOS = useRef<boolean>(typeof navigator !== 'undefined' ? /iPad|iPhone|iPod/.test(navigator.userAgent) : false)
  const isAndroid = useRef<boolean>(typeof navigator !== 'undefined' ? /Android/.test(navigator.userAgent) : false)

  const debounce = (func: (...args: unknown[]) => void, delay: number) => {
    return (...args: unknown[]) => {
      if (inputTimeout.current) clearTimeout(inputTimeout.current)
      inputTimeout.current = setTimeout(() => func(...args), delay)
    }
  }

  const clearAllTimeouts = useCallback(() => {
    if (resizeTimeout.current) clearTimeout(resizeTimeout.current)
    if (inputTimeout.current) clearTimeout(inputTimeout.current)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Only run effect if scrollContainer is valid
    if (!scrollContainer) return

    const scheduleAdjustScroll = (delay = 0) => {
      clearAllTimeouts()
      resizeTimeout.current = setTimeout(adjustScroll, delay)
    }

    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement
      if (['INPUT', 'TEXTAREA'].includes(target.tagName)) {
        focusedInput.current = target

        if (isIOS.current) {
          scheduleAdjustScroll(iosExtraDelay)
        } else if (isAndroid.current) {
          scheduleAdjustScroll(50)
        } else {
          scheduleAdjustScroll(10)
        }
      }
    }

    const handleBlur = () => {
      focusedInput.current = null
      clearAllTimeouts()
    }

    const handleResize = () => {
      if (!focusedInput.current) return

      if (isIOS.current) {
        scheduleAdjustScroll(iosExtraDelay)
      } else {
        scheduleAdjustScroll(10)
      }
    }

    const handleInput = debounce((event: Event) => {
      const target = event.target as HTMLElement
      if (adjustForTextarea && target.tagName === 'TEXTAREA' && focusedInput.current === target) {
        adjustScroll()
      }
    }, 100)

    function adjustScroll() {
      if (!focusedInput.current) return

      const element = focusedInput.current
      if (!document.body.contains(element)) {
        focusedInput.current = null
        return
      }

      const rect = element.getBoundingClientRect()
      const viewportHeight = scrollContainer === window ? (window.visualViewport?.height || window.innerHeight) : (scrollContainer as HTMLElement).clientHeight

      const visibleBottom = viewportHeight - scrollPadding - offsetTop
      const inputBottomPosition = rect.bottom - offsetTop

      const scrollNeeded = inputBottomPosition > visibleBottom
      const scrollAmount = scrollNeeded ? inputBottomPosition - visibleBottom + 5 : 0

      if (scrollNeeded) {
        scrollElementIntoView(scrollContainer, scrollAmount)
      }
    }

    function scrollElementIntoView(container: Window | HTMLElement, scrollAmount: number) {
      if (container === window) {
        if (enableAnimation && 'scrollBehavior' in document.documentElement.style) {
          window.scrollBy({ top: scrollAmount, behavior: 'smooth' })
        } else {
          window.scrollBy(0, scrollAmount)
        }
      } else {
        if (enableAnimation && 'scrollBehavior' in document.documentElement.style) {
          (container as HTMLElement).scrollBy({ top: scrollAmount, behavior: 'smooth' })
        } else {
          (container as HTMLElement).scrollTop += scrollAmount
        }
      }
    }

    document.addEventListener('focusin', handleFocus)
    document.addEventListener('focusout', handleBlur)

    if (scrollContainer === window) {
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize)
      } else {
        window.addEventListener('resize', handleResize)
      }
    } else {
      (scrollContainer as HTMLElement).addEventListener('scroll', handleResize)
    }

    if (adjustForTextarea) {
      document.addEventListener('input', handleInput)
    }

    return () => {
      document.removeEventListener('focusin', handleFocus)
      document.removeEventListener('focusout', handleBlur)

      if (scrollContainer === window) {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleResize)
        } else {
          window.removeEventListener('resize', handleResize)
        }
      } else {
        (scrollContainer as HTMLElement).removeEventListener('scroll', handleResize)
      }

      if (adjustForTextarea) {
        document.removeEventListener('input', handleInput)
      }

      clearAllTimeouts()
    }
  }, [
    scrollPadding,
    offsetTop,
    iosExtraDelay,
    enableAnimation,
    animationDuration,
    adjustForTextarea,
    scrollContainer,
    clearAllTimeouts,
  ])
}

// import useScrollIntoViewOnFocus from './useScrollIntoViewOnFocus';

// function MyComponent() {
//   const scrollRef = useRef(null);
//   useScrollIntoViewOnFocus({ scrollContainer: scrollRef.current, scrollPadding: 30 });;

//   return (
//     <div ref={scrollRef} style={{ overflow: 'auto', height: '100vh' }}>
//       <input type="text" />
//       <textarea rows="5" />
//     </div>
//   );
// }

//       </div>
//   );
// }
